import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { IonIcon } from '@ionic/angular/standalone';
import { Camera } from '@mediapipe/camera_utils';
import { FaceMesh, } from '@mediapipe/face_mesh';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent } from '@qid/super';
import { FaceCaptureUtil } from "./face-capture.util";

@Component({
  selector: 'face-capture',
  templateUrl: './face-capture.component.html',
  styleUrls: ['./face-capture.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SexyModule,
    SuperButtonComponent,
    IonIcon,
  ],
})
export class FaceCapture implements OnDestroy, AfterViewInit {

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild('cameraResultCanvas') cameraResultCanvas: ElementRef<HTMLCanvasElement>;

  @Output('onCapture') onCapture = new EventEmitter<{
    imageData: string, isLive: boolean
  }>();

  public isFaceDetected: boolean = false;
  public blinkDetected: boolean = false;
  public motionVariance: number = 0;
  public depthVariance: number = 0;
  public prevLandmarks: any[] = [];
  public observationStartTime: number = null;
  public isObserving: boolean = false;
  public canvasCtx!: CanvasRenderingContext2D;

  public isCaptured: boolean = false;
  public isConfirming: boolean = false;
  public isFaceInsideOval: boolean = false;

  public camera: Camera
  public cameraCapturedImageData = null;
  public slides = {
    instructions: 'instructions',
    capture: 'capture',
    permission: 'permission',
    confirm: 'confirm',
  }
  public activeSlide = this.slides.instructions
  public cameraPermissionError: boolean = false;
  public permissionErrorMessage: string = '';
  private readonly defaultPermissionDeniedMessage =
    'It looks like you have denied camera access. Please follow the steps below to allow access.';

  // Resource tracking for cleanup
  private faceMesh: FaceMesh | null = null;
  private faceMeshWarmupPromise: Promise<void> | null = null;
  private visibilityCleanup: (() => void) | null = null;
  private isDestroyed = false;
  private isInitializingFaceMesh = false;
  /** When face was last missing; null while a face is present. */
  private noFaceSinceMs: number | null = null;
  private isSoftResettingFaceMesh = false;
  private lastFaceMeshSoftResetMs = 0;
  /** Restart detection after this long without a face (stuck tracker recovery). */
  private readonly noFaceSoftResetMs = 3500;
  /** Minimum gap between soft resets so we don't thrash. */
  private readonly faceMeshSoftResetCooldownMs = 2500;

  constructor(
    private cdr: ChangeDetectorRef
  ) {
  }

  private showCameraPermissionDenied(message?: string): void {
    // Stop camera/streams if they were partially started.
    try {
      this.camera?.stop();
    } catch { /* ignore */ }

    this.cameraPermissionError = true;
    this.permissionErrorMessage = message ?? this.defaultPermissionDeniedMessage;
    this.activeSlide = this.slides.permission;
    this.cdr.detectChanges();
  }

  private clearCameraPermissionError(): void {
    this.cameraPermissionError = false;
    this.permissionErrorMessage = '';
  }

  ngAfterViewInit(): void {
    // Initialize canvas context once
    if (this.canvasElement?.nativeElement) {
      this.canvasCtx = this.canvasElement.nativeElement.getContext('2d') as CanvasRenderingContext2D;
    }

    // Warm the FaceMesh WASM in advance so the first real frame is responsive.
    this.faceMeshWarmupPromise = this.prewarmFaceMesh();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.noFaceSinceMs = null;

    // Stop camera and clean up MediaPipe
    if (this.camera) {
      this.camera.stop();
    }

    if (this.faceMesh) {
      this.faceMesh.close();
    }

    // Stop video streams
    if (this.videoElement?.nativeElement?.srcObject) {
      const stream = this.videoElement.nativeElement.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }

    this.detachCameraVisibilityListener();
  }

  private async initializeFaceMesh(): Promise<void> {
    if (this.isDestroyed || this.isInitializingFaceMesh) return;

    const videoElement = this.videoElement?.nativeElement;
    if (!videoElement) {
      console.error('Video element not available for face mesh');
      return;
    }

    this.isInitializingFaceMesh = true;

    // Prime camera permission first so we can reliably show the
    // permission-denied UI if the user rejects access.
    try {
      const permissionStream = await navigator.mediaDevices.getUserMedia({ video: true });
      permissionStream.getTracks().forEach(track => track.stop());
    } catch (error) {
      this.showCameraPermissionDenied(this.defaultPermissionDeniedMessage);
      this.isInitializingFaceMesh = false;
      return;
    }

    try {
      await this.faceMeshWarmupPromise;

      this.faceMesh = this.faceMesh ?? this.createFaceMesh();
      this.noFaceSinceMs = null;

      const cameraResult = FaceCaptureUtil.startCamera(
        () => (this.isSoftResettingFaceMesh ? null : this.faceMesh),
        videoElement
      );
      this.camera = cameraResult.camera;
      this.visibilityCleanup = cameraResult.cleanup;

    } catch (error) {
      console.error('Error initializing FaceMesh:', error);
      // Fallback: just stop in place; caller can decide what to do next.
      this.cdr.detectChanges();
    } finally {
      this.isInitializingFaceMesh = false;
    }
  }

  private createFaceMesh(): FaceMesh {
    const faceMesh = new FaceMesh({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
      },
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    faceMesh.onResults(this.onFaceLandmarkResults);

    return faceMesh;
  }

  private async prewarmFaceMesh(): Promise<void> {
    if (this.isDestroyed || this.faceMesh) {
      return;
    }

    try {
      const faceMesh = this.createFaceMesh();
      this.faceMesh = faceMesh;

      const offscreenCanvas = document.createElement('canvas');
      offscreenCanvas.width = 1;
      offscreenCanvas.height = 1;

      await faceMesh.send({ image: offscreenCanvas });
    } catch (error) {
      console.error('Error prewarming FaceMesh:', error);
      this.faceMesh?.close();
      this.faceMesh = null;
    }
  }


  private onFaceLandmarkResults = (results: any) => {
    if (this.isDestroyed || this.isCaptured) {
      return;
    }

    const canvasElement = this.canvasElement?.nativeElement;
    if (!canvasElement || !this.canvasCtx) {
      return;
    }

    if (!results || !results.multiFaceLandmarks || !results.multiFaceLandmarks[0]) {
      this.isFaceDetected = false;
      this.isFaceInsideOval = false;
      this.resetMarks();
      this.scheduleNoFaceSoftReset();
      this.cdr.detectChanges();
      return
    }

    this.noFaceSinceMs = null;

    const face = results.multiFaceLandmarks[0];
    const landMarks: [number, number, number][] = [];

    this.canvasCtx.save();
    this.canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
    this.canvasCtx.drawImage(results.image, 0, 0, canvasElement.width, canvasElement.height);

    face.forEach((landmark: any) => {
      const { x, y, z } = landmark;
      landMarks.push([x, y, z]);
    });

    this.canvasCtx.restore();
    this.detectMovement(landMarks);
  };

  /**
   * MediaPipe tracking can stall with a live video but no detections.
   * Swap FaceMesh in place (camera stays up) after a quiet stretch so recovery
   * is invisible to the user.
   */
  private scheduleNoFaceSoftReset(): void {
    if (
      this.isDestroyed ||
      this.isCaptured ||
      this.isSoftResettingFaceMesh ||
      this.isInitializingFaceMesh ||
      !this.camera ||
      !this.faceMesh
    ) {
      return;
    }

    const now = Date.now();
    if (this.noFaceSinceMs == null) {
      this.noFaceSinceMs = now;
      return;
    }

    if (now - this.noFaceSinceMs < this.noFaceSoftResetMs) {
      return;
    }

    if (now - this.lastFaceMeshSoftResetMs < this.faceMeshSoftResetCooldownMs) {
      return;
    }

    void this.softResetFaceMesh();
  }

  private async softResetFaceMesh(): Promise<void> {
    if (
      this.isDestroyed ||
      this.isCaptured ||
      this.isSoftResettingFaceMesh ||
      !this.faceMesh
    ) {
      return;
    }

    this.isSoftResettingFaceMesh = true;
    this.lastFaceMeshSoftResetMs = Date.now();

    const previous = this.faceMesh;

    try {
      // Swap first so the live camera loop picks up the new graph on the next
      // frame; keep video running so the user never sees a restart.
      this.faceMesh = this.createFaceMesh();

      try {
        previous.close();
      } catch {
        // Ignore close races with an in-flight send.
      }

      // Restart the no-face window so we wait a full quiet period again.
      this.noFaceSinceMs = Date.now();
    } catch (error) {
      console.warn('FaceMesh soft reset failed:', error);
      if (!this.faceMesh || this.faceMesh === previous) {
        this.faceMesh = previous;
      }
    } finally {
      this.isSoftResettingFaceMesh = false;
    }
  }

  private detectMovement(landMarks) {
    if (this.isDestroyed) return;
    this.isFaceDetected = true;

    if (landMarks?.length < 1) {
      this.isFaceDetected = false;
      this.resetMarks();
      this.scheduleNoFaceSoftReset();
      this.cdr.detectChanges();
      return;
    }

    this.noFaceSinceMs = null;
    this.updateFaceInsideOvalStatus(landMarks);
    this.cdr.detectChanges();
  }

  private updateFaceInsideOvalStatus(landMarks: [number, number, number][]) {
    const nose = landMarks[1];
    const forehead = landMarks[10];
    const chin = landMarks[152];
    const leftFace = landMarks[234];
    const rightFace = landMarks[454];

    if (!nose || !forehead || !chin || !leftFace || !rightFace) {
      this.isFaceInsideOval = false;
      return;
    }

    const centerX = 0.5;
    const centerY = 0.42;
    const radiusX = 0.225;
    const radiusY = 0.315;

    const isInside = (point: [number, number, number]) => {
      const normalizedX = (point[0] - centerX) / radiusX;
      const normalizedY = (point[1] - centerY) / radiusY;
      return (normalizedX * normalizedX + normalizedY * normalizedY) <= 1;
    };

    this.isFaceInsideOval = [nose, forehead, chin, leftFace, rightFace].every(isInside);
  }

  private resetPassiveObservationState() {
    this.blinkDetected = false;
    this.motionVariance = 0;
    this.depthVariance = 0;
    this.prevLandmarks = [];
    this.isObserving = false;
    this.observationStartTime = null;
  }
  capture() {
    if (this.isDestroyed) return;
    if (!this.isFaceInsideOval) return;

    this.isCaptured = true;

    try {
      const videoElement = this.videoElement?.nativeElement;
      if (!videoElement) {
        throw new Error('Video element not available');
      }

      const imageData = FaceCaptureUtil.captureCameraFeed({
        videoElement: videoElement,
        cameraResultCanvas: this.cameraResultCanvas
      });

      if (!imageData) {
        throw new Error('Capture failed: video dimensions not ready');
      }

      if (this.camera) {
        this.camera.stop();
      }
      // Tab-visibility handler would call camera.start() on return; remove it once capture is final.
      this.detachCameraVisibilityListener();

      this.cameraCapturedImageData = imageData;
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error during capture:', error);
      this.isCaptured = false;
      this.cdr.detectChanges();
    }
  }

  private detachCameraVisibilityListener(): void {
    if (this.visibilityCleanup) {
      this.visibilityCleanup();
      this.visibilityCleanup = null;
    }
  }

  /** Re-attach after retake when MediaPipe camera is running again. */
  private attachCameraVisibilityListenerIfNeeded(): void {
    if (!this.camera || this.visibilityCleanup) {
      return;
    }
    this.visibilityCleanup = FaceCaptureUtil.attachCameraVisibilityListener(this.camera);
  }

  resetMarks() {
    this.resetPassiveObservationState();
    this.isFaceInsideOval = false;
    this.isCaptured = false;
    this.isConfirming = false;
  }

  onStartScanClicked() {

    if (this.isDestroyed) return;

    this.clearCameraPermissionError();
    this.activeSlide = this.slides.capture;
    this.cdr.detectChanges();

    // Wait for one paint cycle so the capture UI is visible before the camera starts.
    requestAnimationFrame(() => {
      if (!this.isDestroyed) this.initializeFaceMesh();
    });
  }

  onPhotoConfirmed(isLive: boolean) {
    if (this.isDestroyed || this.isConfirming) return;
    this.isConfirming = true;
    this.cdr.detectChanges(); // ensure loader renders before the parent transitions
    this.onCapture.emit({
      imageData: this.cameraCapturedImageData,
      isLive: isLive
    })
  }

  onRetakeClicked() {
    if (this.isDestroyed || this.isConfirming) return;

    this.isFaceDetected = false;
    this.cameraCapturedImageData = null;
    this.isCaptured = false;
    this.noFaceSinceMs = null;

    if (this.camera) {
      this.camera.start();
      this.attachCameraVisibilityListenerIfNeeded();
    }

    this.resetMarks();
    this.cdr.detectChanges();
  }

  onReloadPermissionsClicked(): void {
    // Browsers often require a page reload after permission changes.
    window.location.reload();
  }

  async checkCameraPermission(): Promise<PermissionState> {
    // Check for basic MediaDevices support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('MediaDevices API not supported');
      this.cameraPermissionError = true;
      this.permissionErrorMessage = this.defaultPermissionDeniedMessage;
      return "denied";
    }

    try {
      // Check if the browser supports the Permissions API
      if (navigator.permissions) {
        const permissionStatus = await navigator.permissions.query({ name: "camera" as PermissionName });
        if (permissionStatus.state === 'denied') {
          this.cameraPermissionError = true;
          this.permissionErrorMessage = this.defaultPermissionDeniedMessage;
        }
        return permissionStatus.state;
      }
    } catch (error) {
      console.error("Permissions API not supported or error checking permission:", error);
    }

    // Default to prompting for permission if Permissions API is not available
    return "prompt";
  }



  async requestCameraAccess(): Promise<MediaStream | null> {
    try {
      return await navigator.mediaDevices.getUserMedia({ video: true });
    } catch (error) {
      console.error("Error accessing camera:", error);
      return null;
    }
  }
}
